import { useMemo } from 'react';

import { Dropdown } from 'primereact/dropdown';
import { Fieldset } from 'primereact/fieldset';
import { InputText } from 'primereact/inputtext';
import { Control, UseFormWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { BreedsAutoComplete } from '@/components/common';
import { CalendarWrapper as Calendar } from '@/components/common/calendar-wrapper';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { FormField } from '@/pages/embryo-details/components/FormField';
import { EmbryoDetailDto } from '@/services/embryo-service';

import { BULK_LOCKED_FIELDS } from './constants';
import { EmbryoFormFieldConfig } from './field-config';
import { pcResOptions, sexOptions } from './form-options';

type EmbryoRecipStatusTabProps = {
  control: Control<EmbryoDetailDto>;
  watch: UseFormWatch<EmbryoDetailDto>;
  isBulkMode: boolean;
  hasManagerWrite?: boolean;
  columns?: number;
};

export function EmbryoRecipStatusTab({
  control,
  watch,
  isBulkMode,
  hasManagerWrite = false,
  columns = 3,
}: EmbryoRecipStatusTabProps) {
  const { t } = useTranslation();
  const addRemoveEmbryoFields = useFeatureFlag('Add_Remove_Embryo_Fields');
  const bulkLockedFieldsEnabled = useFeatureFlag(
    'Embryo_Bulk_Edit_Locked_Fields',
  );

  const recipBreedId = watch('recipBreedId');
  const recipBreedName = watch('recipBreedName');
  const recipBreedDefaultValue = useMemo(() => {
    if (isBulkMode || !recipBreedId) return undefined;
    if (
      typeof recipBreedId === 'object' &&
      recipBreedId !== null &&
      'id' in recipBreedId
    ) {
      return {
        ...(recipBreedId as any),
        code: (recipBreedId as any).code ?? '',
      };
    }
    return { id: recipBreedId, name: recipBreedName ?? '', code: '' };
  }, [isBulkMode, recipBreedId, recipBreedName]);

  const pcFieldConfigs: EmbryoFormFieldConfig[] = useMemo(
    () => [
      {
        name: 'calfSex',
        label: t('embryo.fetalSex'),
        component: Dropdown,
        componentProps: {
          options: sexOptions,
          placeholder: t('common.selectOption'),
          showClear: true,
        },
      },
      {
        name: 'pc1Date',
        label: t('embryo.pc1Date'),
        component: Calendar,
        componentProps: { dateFormat: 'mm/dd/yy' },
      },
      {
        name: 'pc1Res',
        label: t('embryo.pc1Result'),
        component: Dropdown,
        componentProps: {
          options: pcResOptions,
          placeHolder: t('common.selectOption'),
        },
      },
      {
        name: 'pc2Date',
        label: t('embryo.pc2Date'),
        component: Calendar,
        componentProps: { dateFormat: 'mm/dd/yy' },
      },
      {
        name: 'pc2Res',
        label: t('embryo.pc2Result'),
        component: Dropdown,
        componentProps: {
          options: pcResOptions,
          placeHolder: t('common.selectOption'),
        },
      },
      {
        name: 'pc3Date',
        label: t('embryo.pc3Date'),
        component: Calendar,
        componentProps: { dateFormat: 'mm/dd/yy' },
      },
      {
        name: 'pc3Res',
        label: t('embryo.pc3Result'),
        component: Dropdown,
        componentProps: {
          options: pcResOptions,
          placeHolder: t('common.selectOption'),
        },
      },
      {
        name: 'pc4Date',
        label: t('embryo.pc4Date'),
        component: Calendar,
        componentProps: { dateFormat: 'mm/dd/yy' },
      },
      {
        name: 'pc4Res',
        label: t('embryo.pc4Result'),
        component: Dropdown,
        componentProps: {
          options: pcResOptions,
          placeHolder: t('common.selectOption'),
        },
      },
      {
        name: 'pc5Date',
        label: t('embryo.pc5Date'),
        component: Calendar,
        componentProps: { dateFormat: 'mm/dd/yy' },
      },
      {
        name: 'pc5Res',
        label: t('embryo.pc5Result'),
        component: Dropdown,
        componentProps: {
          options: pcResOptions,
          placeHolder: t('common.selectOption'),
        },
      },
      ...(!addRemoveEmbryoFields
        ? [
            {
              name: 'pc6Date',
              label: t('embryo.pc6Date'),
              component: Calendar,
              componentProps: { dateFormat: 'mm/dd/yy' },
            },
            {
              name: 'pc6Res',
              label: t('embryo.pc6Result'),
              component: Dropdown,
              componentProps: {
                options: pcResOptions,
                placeHolder: t('common.selectOption'),
              },
            },
            {
              name: 'pc7Date',
              label: t('embryo.pc7Date'),
              component: Calendar,
              componentProps: { dateFormat: 'mm/dd/yy' },
            },
            {
              name: 'pc7Res',
              label: t('embryo.pc7Result'),
              component: Dropdown,
              componentProps: {
                options: pcResOptions,
                placeHolder: t('common.selectOption'),
              },
            },
            {
              name: 'pc8Date',
              label: t('embryo.pc8Date'),
              component: Calendar,
              componentProps: { dateFormat: 'mm/dd/yy' },
            },
            {
              name: 'pc8Res',
              label: t('embryo.pc8Result'),
              component: Dropdown,
              componentProps: {
                options: pcResOptions,
                placeHolder: t('common.selectOption'),
              },
            },
            {
              name: 'pc9Date',
              label: t('embryo.pc9Date'),
              component: Calendar,
              componentProps: { dateFormat: 'mm/dd/yy' },
            },
            {
              name: 'pc9Res',
              label: t('embryo.pc9Result'),
              component: Dropdown,
              componentProps: {
                options: pcResOptions,
                placeHolder: t('common.selectOption'),
              },
            },
          ]
        : []),
    ],
    [addRemoveEmbryoFields, t],
  );

  const lockedFieldConfigs: EmbryoFormFieldConfig[] = useMemo(
    () => [
      ...(!addRemoveEmbryoFields
        ? [
            {
              name: 'scHerdman',
              label: t('embryo.herdman'),
              component: InputText,
              requiresManagerWrite: true,
            },
            {
              name: 'scHnDate',
              label: t('embryo.hnDate'),
              component: Calendar,
              componentProps: { dateFormat: 'mm/dd/yy' },
              requiresManagerWrite: true,
            },
            {
              name: 'scIpDate',
              label: t('embryo.ipDate'),
              component: Calendar,
              componentProps: { dateFormat: 'mm/dd/yy' },
              requiresManagerWrite: true,
            },
            {
              name: 'scRpDate',
              label: t('embryo.rpDate'),
              component: Calendar,
              componentProps: { dateFormat: 'mm/dd/yy' },
              requiresManagerWrite: true,
            },
          ]
        : []),
      {
        name: 'scPreviousHeat',
        label: t('embryo.previousHeatDate'),
        component: Calendar,
        componentProps: { dateFormat: 'mm/dd/yy' },
        requiresManagerWrite: true,
      },
      {
        name: 'scLastHeat',
        label: t('embryo.lastHeatDate'),
        component: Calendar,
        componentProps: { dateFormat: 'mm/dd/yy' },
        requiresManagerWrite: true,
      },
      ...(!addRemoveEmbryoFields
        ? [
            {
              name: 'scSyDate',
              label: t('embryo.syDate'),
              component: Calendar,
              componentProps: { dateFormat: 'mm/dd/yy' },
              requiresManagerWrite: true,
            },
          ]
        : []),
    ],
    [addRemoveEmbryoFields, t],
  );

  const recipInfoFieldConfigs: EmbryoFormFieldConfig[] = useMemo(
    () => [
      {
        name: 'recipBreedId',
        label: t('embryo.recipBreed'),
        component: BreedsAutoComplete,
        componentProps: {
          defaultValue: recipBreedDefaultValue,
        },
      },
      {
        name: 'recipBirthDate',
        label: t('embryo.recipBirthDate'),
        component: Calendar,
        componentProps: { dateFormat: 'mm/dd/yy' },
      },
      {
        name: 'recipCalvingDate',
        label: t('embryo.recipCalvingDate'),
        component: Calendar,
        componentProps: { dateFormat: 'mm/dd/yy' },
      },
      {
        name: 'recipRegNum',
        label: t('embryo.recipRegNumber'),
        component: InputText,
      },
    ],
    [t, recipBreedDefaultValue],
  );

  // Heat-history dates are locked during bulk edits (WI 37965).
  const isBulkLocked = (name: string) =>
    isBulkMode &&
    bulkLockedFieldsEnabled &&
    (BULK_LOCKED_FIELDS as readonly string[]).includes(name);

  const allFields = [
    ...pcFieldConfigs,
    ...lockedFieldConfigs.map((field) => ({
      ...field,
      disabled:
        (field.requiresManagerWrite === true && !hasManagerWrite) ||
        isBulkLocked(field.name),
    })),
    ...recipInfoFieldConfigs,
  ].map((field) =>
    isBulkLocked(field.name) ? { ...field, disabled: true } : field,
  );

  return (
    <div className="recip-status-tab">
      <Fieldset
        pt={{
          root: { className: 'p-2' },
          content: { className: 'space-y-2' },
        }}
      >
        <div
          className="grid gap-x-6 gap-y-1"
          style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
        >
          {allFields.map((field) => (
            <FormField
              key={field.name}
              label={field.label}
              name={field.name}
              control={control}
              component={field.component}
              disabled={'disabled' in field ? (field as any).disabled : false}
              required={field.required}
              componentProps={field.componentProps}
            />
          ))}
        </div>
      </Fieldset>
    </div>
  );
}
